import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Box, SystemProps, Flex } from "@storyofams/react-ui";
import { findIndex } from "lodash";
import { isEqual } from "lodash";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import { defineMessages, FormattedMessage } from "react-intl";

import { showToast } from "~lib";
import { Mail } from "~components/common/Icons";
import backInStockNotification from "~lib/shopify/utils/backInStockNotification";
import { countDiscount } from "~lib/shopify/utils/countDiscount";
import { Text, UpsellModal, Modal, Input } from "../../components";
import { useShopify } from "../../context";
import {
  ProductByHandleQuery,
  ProductRecommendationsQuery,
  SelectedOptionInput,
} from "../../lib/shopify/sdk";
import { renderPriceRange, shopifyToIntlPrice } from "../../lib/shopify/utils";
import { Button } from "../common/Button";

import { AddToWishlistButton, OptionSelector } from "./components";
import { GiftCardModal } from "./GiftCardModal";
import { TieredDiscount } from "./TieredDiscount";

const messages = defineMessages({
  addToCart: "Bestel nu!",
});

interface Props {
  product: ProductByHandleQuery["productByHandle"];
  productRecommendations: ProductRecommendationsQuery["productRecommendations"];
  tiered_discount_enabler?: boolean;
  isUpsellDisabled?: boolean;
  content?: any;
}
export const giftCardId = "gid://shopify/Product/6174917508";
export const VariantSelection = React.memo(
  ({
    product,
    productRecommendations,
    isUpsellDisabled,
    tiered_discount_enabler,
    content,
    ...props
  }: Props & SystemProps) => {
    const { query, replace, isReady } = useRouter();
    const posthog = usePostHog();
    const { addItemToCart, updatingCart } = useShopify();
    const [variantId, setSelectedVariantId] = useState(null);
    const [modal, showModal] = useState(null);
    const [open, isOpen] = useState(null);
    const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
    const [notificationVariant, setNotificationVariant] = useState("E-mail");
    const [client, changeClient] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [selectedOptions, setSelectedOptions] = useState<
      SelectedOptionInput[]
    >([]);
    const [showDiscounts, setShowDiscounts] = useState(false);
    const closeGiftModal = () => setIsGiftModalOpen(false);
    const hasOptions = product?.options?.[0]?.values?.length > 1;
    const code = product?.metafields?.find(
      (item) => item?.key === "giftcard_discount"
    )?.value;
    const handleClick = () => {
      const response = backInStockNotification(
        notificationVariant,
        product.id,
        variantId,
        client
      );
      response.then((res) => {
        if (res.data.status === "OK") {
          showToast("Verzonden!", "success");
          isOpen(false);
          return;
        }
        switch (Object.keys(res.data.errors)[0]) {
          case "message":
            showToast("Voeg een e-mailadres of telefoonnummer toe", "error");
            break;
          case "email":
            showToast(
              "Het e-mailadres dat je hebt ingevoerd is niet correct",
              "error"
            );
            break;
          case "variant_no":
            showToast("Kies een productoptie", "error");
            break;
          case "phone_number":
            showToast(
              "Het telefoonnummer dat je hebt ingevoerd is ongeldig. Voeg een telefoonnummer toe met de landcode",
              "error"
            );
            break;
          default:
            showToast("Je staat al ingeschreven!", "info");
            break;
        }
      });
    };
    useEffect(() => {
      if (isReady && query?.variantId) {
        const variant = product?.variants?.edges?.find(
          ({ node }) =>
            node.id === `gid://shopify/ProductVariant/${query?.variantId}`
        );
        if (variant && variant?.node) {
          setSelectedVariantId(variant?.node.id);
          setSelectedOptions(variant?.node.selectedOptions);
        }
      } else if (isReady) {
        setQuantity(1);
        const firstInStock = findIndex(
          product?.variants?.edges,
          function (item) {
            return item.node.availableForSale;
          }
        );
        if (firstInStock >= 0) {
          setSelectedVariantId(
            product?.variants?.edges?.[firstInStock]?.node.id
          );
          setSelectedOptions(
            product?.variants?.edges?.[firstInStock]?.node.selectedOptions
          );
        } else {
          setSelectedVariantId(product?.variants?.edges?.[0]?.node.id);
          setSelectedOptions(
            product?.variants?.edges?.[0]?.node.selectedOptions
          );
        }
      }
    }, [isReady, product]);

    useEffect(() => {
      const variantBySelectedOptions = product.variants.edges.find((item) =>
        isEqual(item.node.selectedOptions, selectedOptions)
      );
      if (variantBySelectedOptions) {
        setSelectedVariantId(variantBySelectedOptions.node.id);
        if (query.variantId !== variantBySelectedOptions.node.id) {
          replace(
            {
              pathname: "/products/[handle]",
              query: {
                ...query,
                variantId: variantBySelectedOptions.node.id.replace(
                  /gid:\/\/shopify\/.*\//i,
                  ""
                ),
                handle: query.handle,
              },
            },
            undefined,
            { shallow: true }
          );
        }
      } else setSelectedVariantId("");
    }, [selectedOptions]);
    useEffect(() => {
      if (!hasOptions) {
        setSelectedVariantId(product?.variants?.edges?.[0]?.node?.id);
      }
    }, [hasOptions]);
    useEffect(() => {
      if (!quantity || quantity < 0) setQuantity(1);
    }, [quantity]);

    const foundVariant = product.variants?.edges?.find(
      ({ node }) => node.id === variantId
    )?.node;
    const discountTag = product.tags.find((item) => item.includes("td"));
    const { twoPcs, fourPcs, sixPcs } = countDiscount(
      foundVariant,
      discountTag
    );
    const discountedPrice = useMemo(() => {
      if (
        foundVariant &&
        product?.autoDiscount &&
        product?.autoDiscount.value !== "0"
      ) {
        return (
          Number(foundVariant.priceV2.amount) -
          (Number(foundVariant.priceV2.amount) *
            Number(product?.autoDiscount?.value)) /
            100
        ).toFixed(2);
      }
      return null;
    }, [foundVariant]);

    const countPrice = useMemo(() => {
      if (discountedPrice)
        return "€" + (Number(discountedPrice) * quantity).toFixed(2);
      if (discountTag) {
        if (quantity === 1)
          return foundVariant
            ? shopifyToIntlPrice(foundVariant?.priceV2)
            : renderPriceRange(product.priceRange, product);
        if (quantity >= 2 && quantity < 4)
          return "€" + (Number(twoPcs.slice(1)) * quantity).toFixed(2);
        if (quantity >= 4 && quantity < 6)
          return "€" + (Number(fourPcs.slice(1)) * quantity).toFixed(2);
        if (quantity >= 6)
          return "€" + (Number(sixPcs.slice(1)) * quantity).toFixed(2);
      } else
        return (
          "€" +
          (
            Number(
              (foundVariant
                ? shopifyToIntlPrice(foundVariant?.priceV2)
                : renderPriceRange(product.priceRange, product)
              ).slice(1)
            ) * quantity
          ).toFixed(2)
        );
    }, [quantity, foundVariant]);
    const discountHandle = useCallback(
      (open: boolean) => setShowDiscounts(open),
      []
    );
    const price = !countPrice.includes("NaN")
      ? countPrice
      : renderPriceRange(product.priceRange, product);
    return (
      <>
        <Box width="100%" {...props}>
          <Box
            display="flex"
            flexDirection={["column", "row"]}
            justifyContent="space-between"
            mb={[1, 4]}
          >
            <Box width={"30%"} mb={["20px", 0]}>
              <Text fontSize={"14px"} color="grey600">
                Prijs
              </Text>
              <Text fontSize={2.5} fontWeight={500} display="flex">
                {foundVariant ? (
                  <Box>
                    {discountedPrice ? (
                      <Flex>
                        {"€" + discountedPrice}
                        <Text
                          color="grey400"
                          css={{ textDecoration: "line-through" }}
                          ml={1.5}
                        >
                          {shopifyToIntlPrice(foundVariant?.priceV2)}
                        </Text>
                      </Flex>
                    ) : (
                      <Flex>
                        {shopifyToIntlPrice(foundVariant?.priceV2)}
                        {foundVariant?.compareAtPriceV2 && (
                          <Text
                            color="grey400"
                            css={{ textDecoration: "line-through" }}
                            ml={1.5}
                          >
                            {shopifyToIntlPrice(foundVariant.compareAtPriceV2)}
                          </Text>
                        )}
                      </Flex>
                    )}
                  </Box>
                ) : (
                  renderPriceRange(product.priceRange, product)
                )}
              </Text>
            </Box>
            {discountTag &&
              variantId &&
              tiered_discount_enabler &&
              !discountedPrice && (
                <TieredDiscount
                  strokeColor={content?.stroke_color?.color}
                  foundVariant={foundVariant}
                  discountTag={discountTag}
                  showDiscounts={showDiscounts}
                  setShowDiscounts={discountHandle}
                />
              )}
          </Box>
          {hasOptions && (
            <Stack
              space={3}
              alignItems="center"
              mb={[2, 4]}
              width="100%"
              flexWrap="wrap"
              flexDirection="column"
            >
              <OptionSelector
                product={product}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
              />
            </Stack>
          )}
          <Text mb={1} fontSize={"14px"} color="grey600">
            Aantal
          </Text>
          <Stack
            space={[1, 1.5]}
            mb={1}
            backgroundColor={"inherit"}
            alignItems={"center"}
            width={"100%"}
          >
            <Box
              px={1}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              height="56px"
              width={["78px", "122px"]}
              border="1px solid grey200"
              borderRadius="md"
              backgroundColor="white"
            >
              <Button
                variant="unstyled"
                fontSize="18px"
                fontWeight={500}
                color="grey400"
                width="15px"
                disabled={quantity < 2}
                onClick={() => {
                  setQuantity((value) => --value);
                  // posthog.capture("Changed Quantity Product Page");
                }}
              >
                -
              </Button>
              {/* <Text fontSize="18px" fontWeight={500}>
              {quantity}
            </Text> */}
              <Box
                width="20px"
                as="input"
                textAlign="center"
                type="number"
                fontFamily="Barlow"
                fontSize="18px"
                fontWeight={500}
                border="none"
                value={quantity}
                onChange={(event) => {
                  setQuantity(Number(event.target.value));
                  // posthog.capture("Changed Quantity Product Page");
                }}
              />
              <Button
                color="grey400"
                textAlign="end"
                width="15px"
                variant="unstyled"
                fontSize="18px"
                fontWeight={500}
                onClick={() => {
                  setQuantity((value) => ++value);
                  // posthog.capture("Changed Quantity Product Page");
                }}
              >
                +
              </Button>
            </Box>
            <Button
              variant="primary"
              fontSize={["16px", 2.25]}
              height="56px"
              fontWeight={500}
              disabled={!variantId || !foundVariant?.availableForSale}
              isLoading={updatingCart && !modal}
              onClick={() => {
                // if (product.id === giftCardId) {
                //   setIsGiftModalOpen(true);
                //   return;
                // }
                posthog.capture("Clicked Add to Cart", {
                  source: "Product Page",
                  product: product.title,
                });
                addItemToCart(
                  [
                    {
                      quantity,
                      merchandiseId: variantId,
                    },
                  ],
                  () => {
                    if (!isUpsellDisabled) showModal(true);
                  }
                );
              }}
              flex={1}
              px={[0, "initial"]}
            >
              <Text>
                <FormattedMessage {...messages.addToCart} /> --
              </Text>
              <Text>{price}</Text>
            </Button>

            <AddToWishlistButton product={product} />
          </Stack>
          <Box display={["block", "none"]}>
            <Stack
              space={[1, 1.5]}
              position={"fixed"}
              bottom={-1}
              zIndex={51}
              left={0}
              mb={1}
              px={2}
              height={"80px"}
              backgroundColor={"white"}
              alignItems={"center"}
              width={"100%"}
              borderTop={"1px solid grey400"}
            >
              <Box
                px={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                height="56px"
                width="80px"
                border="1px solid grey200"
                borderRadius="md"
                backgroundColor="white"
              >
                <Button
                  variant="unstyled"
                  fontSize="18px"
                  fontWeight={500}
                  color="grey400"
                  width="15px"
                  disabled={quantity < 2}
                  onClick={() => {
                    setQuantity((value) => --value);
                    // posthog.capture("Changed Quantity Product Page");
                  }}
                >
                  -
                </Button>
                {/* <Text fontSize="18px" fontWeight={500}>
              {quantity}
            </Text> */}
                <Box
                  width="20px"
                  as="input"
                  textAlign="center"
                  type="number"
                  fontFamily="Barlow"
                  fontSize="18px"
                  fontWeight={500}
                  border="none"
                  value={quantity}
                  onChange={(event) => {
                    setQuantity(Number(event.target.value));
                    // posthog.capture("Changed Quantity Product Page");
                  }}
                />
                <Button
                  color="grey400"
                  textAlign="end"
                  width="15px"
                  variant="unstyled"
                  fontSize="18px"
                  fontWeight={500}
                  onClick={() => {
                    setQuantity((value) => ++value);
                    // posthog.capture("Changed Quantity Product Page");
                  }}
                >
                  +
                </Button>
              </Box>
              <Button
                variant="primary"
                fontSize={["16px", 2.25]}
                height="56px"
                fontWeight={500}
                disabled={!variantId || !foundVariant?.availableForSale}
                isLoading={updatingCart && !modal}
                onClick={() => {
                  // if (product.id === giftCardId) {
                  //   setIsGiftModalOpen(true);
                  //   return;
                  // }
                  posthog.capture("Clicked Add to Cart", {
                    source: "Product Page",
                    product: product.title,
                  });
                  addItemToCart(
                    [
                      {
                        quantity,
                        merchandiseId: variantId,
                      },
                    ],
                    () => {
                      if (!isUpsellDisabled) showModal(true);
                    }
                  );
                }}
                flex={1}
                px={[0, "initial"]}
              >
                <Text>
                  <FormattedMessage {...messages.addToCart} /> --
                </Text>
                <Text>{price}</Text>
              </Button>

              <AddToWishlistButton product={product} isPP />
            </Stack>
          </Box>
          {variantId && !foundVariant?.availableForSale && (
            <>
              <Stack
                space={[1, 1.5]}
                backgroundColor={"none"}
                alignItems={"center"}
                width={"100%"}
              >
                <Flex flexDirection={"column"} flex={1}>
                  <Button
                    icon={Mail}
                    width="100%"
                    height="57px"
                    fontSize={2.25}
                    onClick={() => isOpen(true)}
                    bg={"black"}
                  >
                    <Text>Hou mij op de hoogte</Text>
                  </Button>

                  <Text textAlign={"center"} mt="3px">
                    Toeleveringen zijn elke vrijdag
                  </Text>
                </Flex>
                <Box
                  minWidth="56px"
                  marginLeft="12px"
                  display={["none", "block"]}
                />
                <Box display={"none"}>
                  <AddToWishlistButton product={product} />
                </Box>
              </Stack>
            </>
          )}
          {variantId && !foundVariant?.availableForSale && (
            <Box display={["block", "none"]}>
              <Stack
                space={[1, 1.5]}
                position={"fixed"}
                bottom={0}
                zIndex={51}
                left={0}
                px={[2, 0]}
                height={"80px"}
                backgroundColor={"none"}
                alignItems={"center"}
                width={"100%"}
                borderTop={"1px solid grey400"}
              >
                <Button
                  icon={Mail}
                  width="100%"
                  height="57px"
                  fontSize={2.25}
                  onClick={() => isOpen(true)}
                  bg={"black"}
                >
                  <Text>Hou mij op de hoogte</Text>
                </Button>
                <Box
                  minWidth="56px"
                  marginLeft="12px"
                  display={["none", "block"]}
                />
                <Box display={["block", "none"]}>
                  <AddToWishlistButton product={product} />
                </Box>
              </Stack>
            </Box>
          )}
        </Box>

        <UpsellModal
          isOpen={modal}
          close={() => showModal(false)}
          product={product}
          productRecommendations={productRecommendations}
        />
        <Modal isOpen={open} close={() => isOpen(false)}>
          <Box m={4}>
            <Box backgroundColor="#F4F3F0" m="-4" pt="4" pb="1" px="4">
              <Text fontSize={4} fontWeight="medium">
                Blijf op de hoogte
              </Text>
              <Text mb={3}>
                We sturen je een notificatie wanneer dit product weer
                beschikbaar is.
              </Text>
            </Box>
            {/* <Text fontSize={2.5} fontWeight="medium" pt={6}>
            Stuur mij een bericht via*
          </Text> */}
            {/* <RadioGroup
            mt={3}
            name={"radio"}
            options={[
              { value: "E-mail", label: "E-mail" },
              { value: "SMS", label: "SMS" },
            ]}
            space={2}
            onChange={(event) => setNotificationVariant(event.target.value)}
            picked={notificationVariant}
          ></RadioGroup> */}
            <Input
              fontSize={2.5}
              fontWeight="medium"
              width="85%"
              label={
                notificationVariant === "E-mail"
                  ? "E-mailadres*"
                  : "Telefoonnummer*"
              }
              mt={8}
              value={client}
              onChange={(event) => changeClient(event.target.value)}
            ></Input>
            <Button mt={3} onClick={handleClick} width="62%">
              <Text fontWeight="500" fontSize="20px" lineHeight="24px">
                Hou mij op de hoogte
              </Text>
            </Button>
          </Box>
        </Modal>
        <GiftCardModal
          isOpen={isGiftModalOpen}
          close={closeGiftModal}
          variantId={variantId}
          quantity={quantity}
          openUpsell={showModal}
          price={price}
          code={code}
        />
      </>
    );
  }
);
